"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, Phone, Mail, Calendar, Facebook, Twitter, Instagram, Linkedin, Loader, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <Mail className="size-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground">Message Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 transition-all duration-300 hover:translate-y-[-2px]">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-background/50" />
              </FormControl>
              <FormDescription>Enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-background/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-114567" {...field} className="bg-background/50" />
              </FormControl>
              <FormDescription>Optional – we may call you to discuss further.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select what you are looking for"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>What Are You Looking For?</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-background/50">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Free Strategy Call">Free Strategy Call</SelectItem>
                <SelectItem value="Service Inquiry">Service Inquiry</SelectItem>
                <SelectItem value="General Question">General Question</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Message <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us a bit about your needs..." className="min-h-[120px] resize-y bg-background/50" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] shadow-lg shadow-primary/20 transition-all duration-300 hover:translate-y-[-2px]" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending...
          </> : <>
            Submit Inquiry
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function StartTheConnection() {
  const {
    ref: leftColRef,
    isVisible: leftColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: rightColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: <MapPin className="size-5 text-primary" />,
    title: "Visit Us",
    content: "123 Creative Lane, Innovation City, CA 90000"
  }, {
    icon: <Phone className="size-5 text-primary" />,
    title: "Call Us",
    content: "(123) 456-7890"
  }, {
    icon: <Mail className="size-5 text-primary" />,
    title: "Email Us",
    content: "hello@creativeconnectors.com"
  }];
  const socialLinks = [{
    icon: <Facebook className="size-5" />,
    href: "#",
    label: "Facebook"
  }, {
    icon: <Twitter className="size-5" />,
    href: "#",
    label: "Twitter"
  }, {
    icon: <Instagram className="size-5" />,
    href: "https://instagram.com",
    label: "Instagram"
  }, {
    icon: <Linkedin className="size-5" />,
    href: "#",
    label: "LinkedIn"
  }];
  return <section id="start-the-connection" className="bg-background bg-[radial-gradient(ellipse_at_top_right,var(--color-muted)_0%,transparent_60%)] py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div ref={leftColRef} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${leftColVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-6 tracking-tight">
                Let's Create Something <span className="italic !text-[hsl(38,92%,50%)]">Extraordinary</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">Whether you're ready to dive into a VIP program or just want to explore possibilities on a strategy call, we're here to guide your authority journey.</p>
            </div>

            <Card className="bg-card border-border shadow-lg shadow-foreground/5 overflow-hidden group hover:shadow-xl hover:shadow-foreground/10 transition-all duration-300">
              <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div className="space-y-2">
                  <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-lg mb-2">
                    <SafeHydrate><Calendar className="size-6 text-primary" /></SafeHydrate>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">Free Strategy Call</h3>
                  <p className="text-sm text-muted-foreground">Book a 30-minute session to discuss your goals.</p>
                </div>
                <Button asChild size="lg" className="w-full sm:w-auto shrink-0 shadow-lg shadow-primary/20 group-hover:bg-primary/90 transition-all duration-300 hover:translate-y-[-2px]">
                  <Link to="/ServicesPage">
                    Schedule Now
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {contactDetails.map((detail, index) => <div key={index} data-index={index} className="flex items-start gap-4">
                  <div className="mt-1 p-2 bg-accent rounded-lg shrink-0">
                    {detail.icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{detail.title}</h4>
                    <p className="text-muted-foreground mt-1">{detail.content}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-4 uppercase tracking-wider">Connect With Us</h4>
              <div className="flex items-center gap-4">
                {socialLinks.map((social, index) => <Link key={index} data-index={index} to={social.href} className="p-2.5 bg-accent text-accent-foreground rounded-full hover:bg-primary hover:text-primary-foreground shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5" aria-label={social.label}>
                    {social.icon}
                  </Link>)}
              </div>
            </div>
          </div>

          <div ref={rightColRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightColVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="border-0 shadow-2xl shadow-foreground/10 overflow-hidden bg-card">
              <div className="bg-primary px-8 py-10 text-primary-foreground relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
                <h3 className="text-3xl font-serif font-bold mb-3 relative z-10">Start The Connection</h3>
                <p className="text-primary-foreground/80 text-lg max-w-md relative z-10">
                  Fill out the form below and our team will get back to you within 24 hours to discuss your next steps.
                </p>
              </div>
              
              <CardContent className="p-8 sm:p-10">
                <FormProvider formId="connection_form_contact" sectionName="start-the-connection" defaultValues={{
                full_name: "",
                email_address: "",
                phone_number: "",
                inquiry_type: "",
                message: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email_address: {
                  required: true,
                  pattern: "email"
                },
                phone_number: {
                  required: true,
                  pattern: "phone"
                },
                inquiry_type: {
                  required: true
                }
              }} submitText="Submit Inquiry" successMessage="Thank you for reaching out! We have received your inquiry and will be in touch shortly.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}